import React, { useEffect, useState } from "react";
import type { TaskStatus } from "./DashBoard";

import {
  Box,
  HStack,
  Heading,
  IconButton,
  Text,
  VStack,
  Input,  Flex,
  Tooltip,
  useBreakpointValue,
  InputGroup,
  Icon,
  InputLeftElement,
} from "@chakra-ui/react";
import { ArrowLeftIcon, ListCheck, SearchIcon } from "lucide-react";
import { getThemeClasses } from "../../utils/themeUtils";
import { useColorMode, useColorModeValue } from "@chakra-ui/react";
import { CheckCircle, AlertCircle, XCircle, Clipboard } from "lucide-react"; // Task icon
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Legend, Title } from "chart.js";
import IconComponent from "./IconComponent"; // New IconComponent
import TaskDetails from "./TaskDetails";
import { User } from "./BurnoutAnalysisMain";
import TaskChart from "./TaskChart";
import CheckoutComingSoon from "./CheckoutComingSoon";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";
import DashBoard from "./DashBoard";
import { IconWrapper } from "../../components/IconWrapper";

// Register chart components
ChartJS.register(ArcElement, Legend, Title);

interface MainComponentProps {
  user: User;
  onBack: () => void;
  bg: string;
  color: string;
  organizationId: string;
  taskDetails: any[]; // Prop for task details
  setTaskDetails: React.Dispatch<React.SetStateAction<any[]>>; // Prop for the setter function
}

const BurnoutDetails: React.FC<MainComponentProps> = ({
  user,
  onBack,
  bg,
  color,
  organizationId,
  taskDetails, // Access taskDetails here
  setTaskDetails,
}) => {
  const [activeSection, setActiveSection] = useState<string>("dashboard");
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);
  const [selectedTask, setSelectedTask] = useState<any | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);

  const handleBurnoutClick = () => setActiveSection("burnout");
  const handleChartClick = () => setActiveSection("chart");
  const handleCheckoutClick = () => setActiveSection("checkout");
  const handleDashBoardClick = () => setActiveSection("dashboard");

  // Filter tasks based on search query
  const filteredTasks = user.assignedTasks?.filter((task: any) =>
    task.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const [validTaskCount, setValidTaskCount] = useState(0);
  const [isValidating, setIsValidating] = useState(true);

  // Validate tasks by checking if they actually exist in Firestore
  useEffect(() => {
    const validateTasks = async () => {
      if (!user.assignedTasks || user.assignedTasks.length === 0) {
        setValidTaskCount(0);
        setIsValidating(false);
        return;
      }

      let validCount = 0;
      const validationPromises = user.assignedTasks.map(async (task: any) => {
        try {
          // Check if task has required properties
          if (!task || !task.id || !task.taskId || !task.projectId || !task.channelId) {
            return false;
          }

          // Fetch the task document from Firestore
          const taskDocRef = doc(
            db,
            `organization/${organizationId}/projects/${task.projectId}/channels/${task.channelId}/tasks/${task.taskId}`
          );
          const snapshot = await getDoc(taskDocRef);

          if (snapshot.exists()) {
            const taskData = snapshot.data();
            if (taskData?.data && Array.isArray(taskData.data)) {
              // Recursive function to check if task exists in main tasks or subtasks
              const findTaskInData = (tasks: any[], targetId: string): boolean => {
                for (const t of tasks) {
                  // Check if this task matches
                  if (t.id === targetId) {
                    return true;
                  }
                  // Check subtasks recursively
                  if (t.subtasks && Array.isArray(t.subtasks)) {
                    if (findTaskInData(t.subtasks, targetId)) {
                      return true;
                    }
                  }
                }
                return false;
              };

              // Check if the user's task actually exists in the task data or its subtasks
              return findTaskInData(taskData.data, task.id);
            }
          }
          return false;
        } catch (error) {
          console.error("Error validating task:", error);
          return false;
        }
      });

      const results = await Promise.all(validationPromises);
      validCount = results.filter(Boolean).length;
      
      setValidTaskCount(validCount);
      setIsValidating(false);
    };

    validateTasks();
  }, [user.assignedTasks, organizationId]);

  const burnoutLevel = validTaskCount;

  // Calculate the burnout percentage based on the number of tasks
  const calculateBurnoutPercentage = (taskCount: number) => {
    if (taskCount <= 0) return 0;
    if (taskCount < 5) return 20;
    if (taskCount <= 10) return 40;
    if (taskCount <= 15) return 60;
    return 80; // High burnout for 21+ tasks
  };

  const burnoutPercentage = calculateBurnoutPercentage(burnoutLevel);

  // Create the chart data
  let burnoutColor = "green"; // Default color (low burnout)

  if (burnoutPercentage >= 65) {
    burnoutColor = "red"; // High burnout
  } else if (burnoutPercentage >= 40) {
    burnoutColor = "#F59E0B"; // Moderate burnout
  }

  // Create the chart data
  const data = {
    datasets: [
      {
        label: "Burnout",
        data: [burnoutPercentage, 100 - burnoutPercentage], // Represents burnout vs non-burnout
        backgroundColor: [burnoutColor, "#CBD5E0"], // Dynamically updated burnout color, gray for non-burnout
        borderWidth: 0,
      },
    ],
  };

  // Chart options
  const options = {
    responsive: true,
    plugins: {
      tooltip: {
        enabled: false, // Disable default tooltip
      },
      legend: {
        display: false, // Hide legend
      },
      datalabels: {
        display: true,
        color: "white",
        font: {
          weight: "bold",
          size: 24,
        },
        formatter: () => `${burnoutPercentage}%`, // Show percentage in center
      },
    },
    cutout: "70%", // Make it a donut chart
  };

  // Define the status text based on the burnout percentage
  const burnoutStatus = () => {
    if (burnoutPercentage >= 80) return "Burnout";
    if (burnoutPercentage >= 40) return "Moderate";
    return "Good";
  };

  // Define Icons for Status
  const statusIcons = {
    good: <CheckCircle color="green" />,
    moderate: <AlertCircle color="#F59E0B" />,
    burnout: <XCircle color="red" />,
  };

  const currentStatus =
    burnoutPercentage >= 80
      ? "burnout"
      : burnoutPercentage >= 40
      ? "moderate"
      : "good";

  const currentIcon = statusIcons[currentStatus];

  // Define responsive layout: on mobile, stack the chart and tasks in a column
  const isMediumScreen = useBreakpointValue({ base: false, md: true });
  const handleTaskClick = (task: any) => {
    const taskData = taskDetails.find(
      (t) => t.id === task.id || t.taskId === task.id
    );

    if (taskData) {
      setSelectedTask(taskData); // Set the found task data
    } else {
      setSelectedTask(null); // If no task is found, set it to null
    }

    // Toggle the task details visibility
    if (expandedTaskId === task.id) {
      setExpandedTaskId(null); // Collapse the task details if the same task is clicked
      setSelectedTask(null); // Clear selected task if collapsed
    } else {
      setExpandedTaskId(task.id); // Expand the clicked task
    }
  };




  return (
    <Box color={color} p={6} borderRadius="md" overflowY="auto">
      <HStack justify="space-between" align="center" mb={5}>
        {/* Left: Heading */}
        <Heading fontSize="lg">
          Burnout Details – {user.firstName} {user.lastName}
        </Heading>

        {/* Right: IconComponent and Back Button */}
        <HStack spacing={4}>
          <IconComponent
            onBurnoutClick={handleBurnoutClick}
            onChartClick={handleChartClick}
            onCheckoutClick={handleCheckoutClick}
            onDashBoardClick={handleDashBoardClick}
          />

          <Tooltip label="Go Back" aria-label="Go Back">
            <IconButton
              icon={<ArrowLeftIcon size={18} />}
              aria-label="Go Back"
              onClick={onBack}
              borderRadius="50%"
              backgroundColor={isDark ? "black" : "#FFF7E6"}
              borderWidth="1px"
              borderColor="#B59143"
              color="#B59143"
              _hover={{ 
                backgroundColor: isDark ? "black" : "#FFF7E6", 
                borderColor: "#FFD700", 
                color: "#FFD700" 
              }}
              _active={{ 
                backgroundColor: isDark ? "black" : "#FFF7E6", 
                borderColor: "#B59143", 
                color: "#B59143" 
              }}
            />
          </Tooltip>
        </HStack>
      </HStack>

      {activeSection === "burnout" && (
        <Flex
          direction={isMediumScreen ? "row" : "column"} // Stack the content vertically for smaller screens
          justify="space-between"
          align="flex-start"
          mb={6}
        >
          {/* Left Section: Chart and Legend */}
          <Box width={{ base: "100%", md: "45%" }} p={4}>
            <VStack spacing={6} alignItems="center">
              <Box
                width={{ base: "100%", md: "80%" }}
                height={{ base: "120px", md: "150px" }}
                display="flex"
                justifyContent="center"
                alignItems="center"
                position="relative"
              >
                <Doughnut data={data} options={options} />
                <Text
                  position="absolute"
                  fontSize="xl"
                  fontWeight="bold"
                  color="black"
                >
                  {burnoutPercentage}%
                </Text>
              </Box>

              {/* Status Text */}
              <HStack spacing={3}>
                {currentIcon}
                <Text
                  fontSize="md"
                  fontWeight="bold"
                  color={
                    currentStatus === "good"
                      ? "green"
                      : currentStatus === "moderate"
                      ? "yellow.400"
                      : "red"
                  }
                >
                  {burnoutStatus()} ({burnoutPercentage}%)
                </Text>
              </HStack>

              {/* Color Legend */}
              <VStack fontSize={"sm"} spacing={3}>
                <HStack>
                  <Box
                    width="20px"
                    height="20px"
                    backgroundColor="green"
                    borderRadius="50%"
                  />
                  <Text>Good Burnout (0% - 40%)</Text>
                </HStack>
                <HStack>
                  <Box
                    width="20px"
                    height="20px"
                    color={"yellow.400"}
                    background="yellow.400"
                    borderRadius="50%"
                  />
                  <Text>Moderate Burnout (41% - 79%)</Text>
                </HStack>
                <HStack>
                  <Box
                    width="20px"
                    height="20px"
                    backgroundColor="red"
                    borderRadius="50%"
                  />
                  <Text>High Burnout (80% - 100%)</Text>
                </HStack>
              </VStack>
            </VStack>
          </Box>

          {/* Right Section: Assigned Tasks and Search */}
          <Box width={{ base: "100%", md: "55%" }} p={4}>
            {/* Conditional Rendering for No Tasks Message */}
            {filteredTasks?.length === 0 ||
            filteredTasks === undefined ||
            filteredTasks === null ? (
              <Box
                height="300px"
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  color="gray.500"
                  textAlign="center"
                >
                  No tasks assigned to this user
                </Text>
              </Box>
            ) : (
              <>
                {/* Search Bar */}
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <Icon as={SearchIcon} color="gray.500" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search Assigned Tasks"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    mb={4}
                    focusBorderColor="#B59143"
                    borderRadius="xl"
                    boxShadow="sm"
                    _hover={{ borderColor: "#B59143" }} // Add a hover effect
                  />
                </InputGroup>

                {/* Assigned Tasks List */}
                <Box
                  maxHeight="300px"
                  overflowY="auto"
                  border="1px solid #CBD5E0"
                  borderRadius="md"
                  p={2}
                >
                  <VStack spacing={2}>
                    {filteredTasks?.map((task: any) => (
                      <Box
                        key={task.id}
                        p={2}
                        bg={"gray.100"}
                        borderRadius="md"
                        width="100%"
                        _hover={{ bg: "#f7fafc", cursor: "pointer" }}
                      >
                        <HStack justify="space-between">
                          <HStack spacing={3}>
                            <ListCheck color="blue" size={"15px"} />
                            <Text fontSize="sm" fontWeight="semibold">
                              {task.name}
                            </Text>
                          </HStack>

                          {/* Toggle button for showing/hiding task details */}
                          <IconButton
                            icon={
                              expandedTaskId === task.id ? (
                                <IconWrapper icon={FaChevronUp} size={16} /> // Show up icon when the task is expanded
                              ) : (
                                <IconWrapper icon={FaChevronDown} size={16} /> // Show down icon when the task is collapsed
                              )
                            }
                            onClick={() => handleTaskClick(task)} // Toggle task details
                            size="sm"
                            variant="ghost"
                            aria-label={""}
                          />
                        </HStack>

                        {/* Conditionally render TaskDetails based on the expanded task */}
                        {expandedTaskId === task.id && (
                          <TaskDetails
                            key={task.id}
                            task={selectedTask || {}}
                          /> // Render empty object if selectedTask is null
                        )}
                      </Box>
                    ))}
                  </VStack>
                </Box>
              </>
            )}
          </Box>
        </Flex>
      )}

      {activeSection === "chart" && (
        <TaskChart
          // pass exactly the same props shape TaskChart expects:
          tasks={taskDetails.map((t) => ({
            id: t.id,
            name: t.name,
            status: "Not-started", // or derive real status if you have it
          }))}
          length={user.assignedTasks?.length || 0}
        />
      )}
      {activeSection === "checkout" && (
        <CheckoutComingSoon
          organizationId={organizationId}
          userId={user?.id || ""}
        />
      )}
      {activeSection === "dashboard" && (
        <DashBoard
        organizationId={organizationId}
        userId={user.id}
        // pass the full taskDetails array straight through:
        tasks={taskDetails.map((t) => ({ createdAt: new Date(t.plannedStart) }))}
        assignedTasks={taskDetails.map((t) => ({
          id: t.id,
          name: t.name,
          status: t.status as TaskStatus,
          risk: t.risk as "Low" | "Medium" | "High",
          plannedStart: t.plannedStart,
        }))}
        totalTasks={taskDetails.length}
      />
      )}
    </Box>
  );
};

export default BurnoutDetails;
